/**
 * Calculate the number of images required for a family tree based on generations
 */
export function calculateRequiredImagesForGenerations(
  generations: number
): number {
  // For family trees, we include both user's and spouse's family lines
  // Generation 0: 2 people (self + spouse)
  // Generation 1: 4 people (2 parents for user + 2 parents for spouse)
  // Generation 2: 8 people (4 grandparents for user + 4 grandparents for spouse)
  // etc.

  let totalImages = 0;

  for (let gen = 0; gen < generations; gen++) {
    if (gen === 0) {
      totalImages += 2; // Self + spouse
    } else {
      totalImages += Math.pow(2, gen + 1); // 2^(generation+1) for each generation
    }
  }

  return totalImages;
}

/**
 * Calculate required images for a specific order based on product and options
 */
export function calculateRequiredImagesForOrder(
  productId: string,
  selectedOptions: Record<string, any>
): number {
  // For family tree products - generations is required
  if (
    productId?.includes("pft") ||
    productId?.includes("gc-basic-family") ||
    productId?.includes("gc-custom-family")
  ) {
    return calculateRequiredImagesForGenerations(selectedOptions.generations);
  }

  // For generational art products - generations is required
  if (productId?.includes("ga")) {
    return selectedOptions.generations; // Generational art is single line
  }

  // For missionary art - fixed number of images
  if (productId?.includes("mpa")) {
    const size = selectedOptions?.size;
    const hasPerimeter = selectedOptions?.perimeter;

    if (size === "large" && hasPerimeter) {
      return 80; // Large with perimeter
    } else if (size === "large") {
      return 40; // Large without perimeter
    } else {
      return 40; // Small (default)
    }
  }

  return 0; // Unknown product type
}

/**
 * Get generation display name for order cards
 */
export function getGenerationDisplayName(generations: number): string {
  return `${generations} Generation${generations !== 1 ? "s" : ""}`;
}

/**
 * Calculate expected generations completed for progress tracking
 */
export function calculateExpectedGenerationsForOrder(
  productId: string,
  selectedOptions: Record<string, any>
): number {
  // For family tree products - generations is required
  if (
    productId?.includes("pft") ||
    productId?.includes("gc-basic-family") ||
    productId?.includes("gc-custom-family")
  ) {
    return selectedOptions.generations;
  }

  // For generational art products - generations is required
  if (productId?.includes("ga")) {
    return selectedOptions.generations;
  }

  return 0; // Not applicable for other products
}

/**
 * Calculate bulk photo enhancement pricing for a product
 * Uses static pricing tiers based on generation count
 */
export function calculatePhotoEnhancementPricing(
  productId: string,
  selectedOptions: Record<string, any>
): {
  photoCount: number;
  individualPrice: number; // Price per photo if ordered individually later
  bulkPrice: number; // Static bulk price based on generation tier
  savings: number; // Amount saved by ordering bulk
  description: string;
} {
  const photoCount = calculateRequiredImagesForOrder(productId, selectedOptions);
  const individualPrice = 5; // $5 per photo for individual enhancement
  
  // Static pricing tiers based on photo count
  let bulkPrice: number;
  if (photoCount <= 5) {
    bulkPrice = 20; // Small projects: $20
  } else if (photoCount <= 15) {
    bulkPrice = 40; // Medium projects: $40
  } else if (photoCount <= 30) {
    bulkPrice = 60; // Large projects: $60
  } else {
    bulkPrice = 80; // Extra large projects: $80
  }
  
  const totalIndividualPrice = photoCount * individualPrice;
  const savings = totalIndividualPrice - bulkPrice;

  // Generate description based on product type
  let description = "";
  if (productId?.includes("pft") || productId?.includes("gc-basic-family") || productId?.includes("gc-custom-family")) {
    const generations = calculateExpectedGenerationsForOrder(productId, selectedOptions);
    description = `Enhance all ${photoCount} photos in your ${generations}-generation family tree`;
  } else if (productId?.includes("ga")) {
    const generations = calculateExpectedGenerationsForOrder(productId, selectedOptions);
    description = `Enhance all ${photoCount} photos in your ${generations}-generation art`;
  } else if (productId?.includes("mpa")) {
    description = `Enhance all ${photoCount} photos in your missionary art`;
  } else {
    description = `Enhance all ${photoCount} photos in your order`;
  }

  return {
    photoCount,
    individualPrice,
    bulkPrice,
    savings: Math.max(0, savings), // Ensure savings is never negative
    description,
  };
}
